import { Injectable } from '@angular/core';
import type { AuthSession } from '@monorepo/types';
import { AUTH_STORAGE_KEYS } from '../../features/auth/config/auth-storage.keys';
import type {
  PotatoLoginResponse,
  StoredAuthTokens,
} from '../../features/auth/models/login-response';

@Injectable({
  providedIn: 'root',
})
export class Auth {
  storeTokens(response: PotatoLoginResponse): void {
    const stored: StoredAuthTokens = {
      ...response,
      expiresAt: this.decodeJwtExpiryMs(response.accessToken),
    };
    localStorage.setItem(AUTH_STORAGE_KEYS.tokens, JSON.stringify(stored));
  }

  getAccessToken(): string | null {
    const tokens = this.getStoredTokens();
    if (!tokens) {
      return null;
    }

    if (Date.now() >= tokens.expiresAt) {
      return null;
    }

    return tokens.accessToken;
  }

  /** Refresh tokens outlive the access token, so this ignores expiresAt. */
  getRefreshToken(): string | null {
    return this.getStoredTokens()?.refreshToken ?? null;
  }

  /** Rehydrates the logged-in session from storage, e.g. after a page reload. */
  getSession(): AuthSession | null {
    const tokens = this.getStoredTokens();
    if (!tokens || Date.now() >= tokens.expiresAt) {
      return null;
    }

    return {
      user: { id: tokens.email, name: tokens.username, email: tokens.email },
      token: tokens.accessToken,
      role: tokens.role,
      permissions: tokens.permissions,
    };
  }

  isAuthenticated(): boolean {
    return this.getAccessToken() !== null;
  }

  clearSession(): void {
    localStorage.removeItem(AUTH_STORAGE_KEYS.tokens);
  }

  private getStoredTokens(): StoredAuthTokens | null {
    const raw = localStorage.getItem(AUTH_STORAGE_KEYS.tokens);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as StoredAuthTokens;
    } catch {
      return null;
    }
  }

  /** The backend doesn't return an expiry, so it's read off the JWT's own `exp` claim. */
  private decodeJwtExpiryMs(token: string): number {
    try {
      const payload = token.split('.')[1] ?? '';
      const base64 = payload
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=');
      const claims = JSON.parse(atob(base64)) as { exp?: number };
      return typeof claims.exp === 'number' ? claims.exp * 1000 : Date.now();
    } catch {
      return Date.now();
    }
  }
}
